/**
 * Aspect — Q/E rotation input (tap vs hold)
 * --------------------------------------------------------------
 * Quick tap: snap to the next 45° step in that direction (e.g. 39° → 45°,
 * then 90°; 56° with Q → 45° → 0° → −45°), animated over a short ease-out.
 * Hold past the delay: slow continuous rotation (degrees per second).
 */

import { normalizeYaw } from "../scene/transforms.js";

export const TAP_STEP_DEG = 45;
export const TAP_ANIM_MS = 200;
export const HOLD_DELAY_MS = 200;
/** Degrees per second while held (matches gizmo fluid speed). */
export const HOLD_SPEED_DEG = 100;

const SNAP_EPS = 0.5;

function shortestYawDelta(from, to) {
  let d = normalizeYaw(to) - normalizeYaw(from);
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

function lerpYaw(from, to, t) {
  return normalizeYaw(from + shortestYawDelta(from, to) * t);
}

/**
 * Next 45° snap in the given direction (+1 = E/CW, −1 = Q/CCW).
 * If already on a snap, advances one more step (45° → 90°, 45° → 0°, etc.).
 */
export function nextSnapYaw(current, sign, step = TAP_STEP_DEG) {
  const y = normalizeYaw(current);
  const s = sign >= 0 ? 1 : -1;
  const flat = y < 0 ? y + 360 : y;

  if (s > 0) {
    let next = Math.ceil((flat - SNAP_EPS) / step) * step;
    if (next <= flat + SNAP_EPS) next += step;
    return normalizeYaw(next >= 360 ? next - 360 : next);
  }

  let next = Math.floor((flat + SNAP_EPS) / step) * step;
  if (next >= flat - SNAP_EPS) next -= step;
  return normalizeYaw(next);
}

export class QERotationInput {
  /**
   * @param {{ getYaw: () => number, setYaw: (y: number) => void, onChange?: () => void }} opts
   */
  constructor(opts) {
    this.getYaw = opts.getYaw;
    this.setYaw = opts.setYaw;
    this.onChange = opts.onChange ?? (() => {});
    this._q = { downAt: 0, holding: false };
    this._e = { downAt: 0, holding: false };
    /** @type {{ from: number, to: number, start: number } | null} */
    this._tapAnim = null;
  }

  reset() {
    this._q = { downAt: 0, holding: false };
    this._e = { downAt: 0, holding: false };
    this._tapAnim = null;
  }

  /** True while a tap tween or key is held — caller should keep ticking. */
  needsTick() {
    return Boolean(this._tapAnim) || Boolean(this._q.downAt) || Boolean(this._e.downAt);
  }

  /** @param {'q'|'e'} key @param {boolean} [repeat] */
  onKeyDown(key, repeat = false) {
    if (repeat) return;
    const k = key.toLowerCase();
    const now = performance.now();
    if (k === "q" && !this._q.downAt) this._q.downAt = now;
    if (k === "e" && !this._e.downAt) this._e.downAt = now;
  }

  /** @param {string} key */
  onKeyUp(key) {
    const k = key.toLowerCase();
    if (k === "q" && this._q.downAt) {
      if (!this._q.holding) this._startTap(-TAP_STEP_DEG);
      this._q = { downAt: 0, holding: false };
    }
    if (k === "e" && this._e.downAt) {
      if (!this._e.holding) this._startTap(TAP_STEP_DEG);
      this._e = { downAt: 0, holding: false };
    }
  }

  /** Call each frame while rotation keys may be held or a tap tween is active. */
  tick(dt) {
    const now = performance.now();
    let changed = false;

    if (this._q.downAt) changed = this._tickSide(this._q, -1, dt, now) || changed;
    if (this._e.downAt) changed = this._tickSide(this._e, 1, dt, now) || changed;

    if (this._tapAnim) {
      const t = Math.min(1, (now - this._tapAnim.start) / TAP_ANIM_MS);
      const eased = 1 - (1 - t) ** 3;
      const yaw = lerpYaw(this._tapAnim.from, this._tapAnim.to, eased);
      this.setYaw(yaw);
      this.onChange();
      if (t >= 1) {
        this.setYaw(this._tapAnim.to);
        this.onChange();
        this._tapAnim = null;
      }
      changed = true;
    }

    return changed;
  }

  _startTap(deltaDeg) {
    const from = this.getYaw();
    const sign = deltaDeg >= 0 ? 1 : -1;
    const to = nextSnapYaw(from, sign);
    this._tapAnim = { from, to, start: performance.now() };
  }

  _tickSide(side, sign, dt, now) {
    if (!side.downAt) return false;
    if (!side.holding && now - side.downAt >= HOLD_DELAY_MS) {
      side.holding = true;
      this._tapAnim = null;
    }
    if (!side.holding) return false;
    this._step(sign * HOLD_SPEED_DEG * dt);
    return true;
  }

  _step(delta) {
    if (!delta) return;
    this.setYaw(normalizeYaw(this.getYaw() + delta));
    this.onChange();
  }
}
